import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Search, Heart, Loader2, Send, Trash2, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Community() {
  const { t } = useLang();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [supplement, setSupplement] = useState("");
  const [helpsWith, setHelpsWith] = useState("");
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/posts", { params: { q: query } });
      setPosts(data.posts || []);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const search = (e) => { e.preventDefault(); load(q); };

  const submit = async (e) => {
    e.preventDefault();
    if (!supplement.trim() || !helpsWith.trim()) return;
    setPosting(true);
    try {
      await api.post("/community/posts", { supplement, helps_with: helpsWith, body });
      setSupplement(""); setHelpsWith(""); setBody("");
      toast.success(t("community.posted"));
      load(q);
    } catch (e) { toast.error(apiError(e)); } finally { setPosting(false); }
  };

  const toggleLike = async (id) => {
    try {
      const { data } = await api.post(`/community/posts/${id}/like`);
      setPosts((prev) => prev.map((p) => (p.id === id ? data : p)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/community/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <Users className="text-emerald-500" size={26} /> {t("community.title")}
        </h1>
        <p className="mt-1 text-sm text-gray-600">{t("community.sub")}</p>
      </div>

      {/* search */}
      <form onSubmit={search} className="mb-5 flex gap-2" data-testid="community-search-form">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("community.searchPlaceholder")} data-testid="community-search-input"
            className="w-full rounded-full border border-gray-200 py-3 pl-10 pr-4 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <button type="submit" data-testid="community-search-button" className="rounded-full bg-emerald-500 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-600">{t("community.search")}</button>
      </form>

      {/* share form */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5" data-testid="community-share-card">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-800"><Sparkles size={15} className="text-emerald-500" /> {t("community.shareTitle")}</h3>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={supplement} onChange={(e) => setSupplement(e.target.value)} placeholder={t("community.supplementPh")} data-testid="community-supplement-input"
              className="rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
            <input value={helpsWith} onChange={(e) => setHelpsWith(e.target.value)} placeholder={t("community.helpsPh")} data-testid="community-helps-input"
              className="rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
          </div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder={t("community.bodyPh")} data-testid="community-body-input"
            className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
          <button type="submit" disabled={posting || !supplement.trim() || !helpsWith.trim()} data-testid="community-post-button"
            className="flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50">
            {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} {t("community.post")}
          </button>
        </form>
        <p className="mt-3 text-xs leading-relaxed text-gray-400">{t("community.disclaimer")}</p>
      </div>

      {/* posts */}
      {loading ? (
        <div className="flex min-h-[160px] items-center justify-center"><Loader2 className="animate-spin text-emerald-500" size={26} /></div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500" data-testid="community-empty">
          {q ? t("community.noResults") : t("community.empty")}
        </div>
      ) : (
        <div className="space-y-3" data-testid="community-list">
          {posts.map((p) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={`community-post-${p.id}`}
              className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">{p.supplement}</span>
                  <span className="text-xs text-gray-400">{t("community.for")}</span>
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{p.helps_with}</span>
                </div>
                {(p.is_mine || user?.role === "admin") && (
                  <button onClick={() => remove(p.id)} data-testid={`community-delete-${p.id}`}
                    title={!p.is_mine && user?.role === "admin" ? t("community.modDelete") : undefined}
                    className={`hover:text-red-500 ${!p.is_mine && user?.role === "admin" ? "text-red-300" : "text-gray-300"}`}><Trash2 size={15} /></button>
                )}
              </div>
              {p.body && <p className="mb-3 text-sm leading-relaxed text-gray-700">{p.body}</p>}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-400">{t("community.by")} {p.author_name}</span>
                <button onClick={() => toggleLike(p.id)} data-testid={`community-like-${p.id}`}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${p.liked ? "border-red-200 bg-red-50 text-red-600" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                  <Heart size={13} fill={p.liked ? "currentColor" : "none"} /> {p.likes}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
